import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  GetProductsResponse,
  ProductUpdateValidResponse,
  UpsertProductResponse,
} from './product-service.model';
import { EnergySystem, LysineSystem } from '../../settings/models/settings.model';
import { Product } from '../../shared/models/shared.model';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  protected baseUrl = `${this.env.API_BASE_URL}/service/pig-feed-advice/secure`;

  constructor(
    protected http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: { API_BASE_URL: string },
    private readonly localeService: LocaleService,
  ) {}

  getProductById(
    id: number,
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
  ): Observable<Product | HttpErrorResponse> {
    const params = new HttpParams()
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));

    return this.http.get<Product>(`${this.baseUrl}/product/${id}`, { params }).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Product>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  getProducts(
    countryCode = '',
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
  ): Observable<Product[] | HttpErrorResponse> {
    const params = new HttpParams()
      .set('country', countryCode)
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));

    return this.http.get<GetProductsResponse>(`${this.baseUrl}/product`, { params }).pipe(
      map((response) => response.products),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Product[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  getProductsByCustomerId(
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
    customerId?: number,
  ): Observable<Product[] | HttpErrorResponse> {
    const params = new HttpParams()
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));
    const customer = customerId ? `/customer/${customerId}` : '';

    return this.http
      .get<GetProductsResponse>(`${this.baseUrl}/product${customer}`, { params })
      .pipe(
        map((response) => response.products),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<Product[]>[]);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  getProductByProductCode(
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
    productCode: string,
  ): Observable<Product | HttpErrorResponse> {
    const params = new HttpParams()
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));

    return this.http
      .get<Product>(`${this.baseUrl}/product/productCode/${productCode}`, { params })
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<Product>{});
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  upsertProduct(product: Product): Observable<UpsertProductResponse | HttpErrorResponse> {
    return this.http.post<UpsertProductResponse>(`${this.baseUrl}/product`, product).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 409) {
          return throwError({
            message: this.localeService.translate('create-product.error-code-already-in-use'),
          });
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  checkUpdateValidityProduct(
    product: Product,
  ): Observable<ProductUpdateValidResponse | HttpErrorResponse> {
    return this.http
      .post<ProductUpdateValidResponse>(`${this.baseUrl}/product/check-update-validity`, product)
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404 || error.status === 409) {
            return of(<Product>{});
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  deleteProduct(productId: number) {
    return this.http.delete(`${this.baseUrl}/product/${productId}`);
  }
}
